import { useState, type FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9a96uknib0mhnm9k27g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: (formData.get('email') as string || '').trim(),
        }).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (!res.ok || parsed?.code !== 'OK') {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Submission failed';
        setStatus('error');
        setErrorMsg(serverMsg);
        return;
      }

      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-accent-900 text-background-200">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-background-50 text-lg font-semibold whitespace-nowrap">
                Superbuy Spread
              </span>
            </div>
            <p className="text-background-400 text-xs leading-relaxed max-w-[220px]">
              The ultimate Superbuy Sheet research platform. Organize, compare and discover the best products across 14 curated categories.
            </p>
          </div>

          <div>
            <h4 className="text-background-300 text-xs font-label uppercase tracking-[1px] mb-5 whitespace-nowrap">Newsletter</h4>
            <form
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="flex items-end gap-0 border-b border-background-500 pb-2"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                required
                className="bg-transparent text-background-100 text-sm placeholder:text-background-500 outline-none flex-1 py-1"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="newsletter-hp"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0 cursor-pointer transition-all hover:bg-accent-400 disabled:opacity-50"
              >
                <i className="ri-arrow-right-line text-background-50 text-sm"></i>
              </button>
            </form>
            {status === 'success' && (
              <p className="text-accent-300 text-xs mt-2">Subscribed! Welcome to the community.</p>
            )}
            {status === 'error' && (
              <p className="text-red-400 text-xs mt-2">{errorMsg}</p>
            )}
          </div>

          <div>
            <h4 className="text-background-300 text-xs font-label uppercase tracking-[1px] mb-5 whitespace-nowrap">Categories</h4>
            <div className="flex flex-col gap-2.5">
              <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">Shoes</a>
              <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">Jackets</a>
              <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">Accessories</a>
            </div>
          </div>

          <div>
            <h4 className="text-background-300 text-xs font-label uppercase tracking-[1px] mb-5 whitespace-nowrap">Social</h4>
            <div className="flex flex-col gap-2.5">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">Main Website</a>
              <a href="/superbuy-sheet-guide" className="text-background-400 text-sm hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">SEO Guide</a>
              <a href="#categories" className="text-background-400 text-sm hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer">Categories</a>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-accent-950/40 py-10 md:py-14 overflow-hidden">
        <p className="font-heading text-[120px] md:text-[200px] lg:text-[280px] text-accent-800/30 leading-none tracking-[-2px] text-center whitespace-nowrap select-none">
          SUPERBUY
        </p>
      </div>

      <div className="border-t border-background-500/20 py-5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-background-500 text-xs whitespace-nowrap">
            &copy; {new Date().getFullYear()} Superbuy Spread. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="/superbuy-sheet-guide" className="text-background-500 text-xs hover:text-background-200 transition-colors whitespace-nowrap cursor-pointer">Guide</a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-500 text-xs hover:text-background-200 transition-colors whitespace-nowrap cursor-pointer">Main Site</a>
          </div>
        </div>
      </div>
    </footer>
  );
}